import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

/**
 * A compact inline "icon + number" metric for table rows (rage clicks, errors,
 * …). Muted to a neutral grey when the value is zero so a busy row's real
 * signals stand out. Shared so the frustration/errors/user lists read alike.
 */
export function IconStat({
  icon,
  value,
  color,
  title,
}: {
  icon: ReactNode;
  value: number;
  color: string;
  title?: string;
}) {
  const on = value > 0;
  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      justifyContent="flex-end"
      title={title}
      sx={{ color: on ? color : 'text.disabled', minWidth: 40 }}
    >
      <Box sx={{ display: 'grid', placeItems: 'center', '& svg': { fontSize: 16 } }}>{icon}</Box>
      <Typography variant="body2" fontWeight={on ? 700 : 400} sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Stack>
  );
}

/** A soft ranked badge (#1, #2 …) — the top few are tinted with the accent. */
export function RankBadge({ rank, accent = '#ef4444' }: { rank: number; accent?: string }) {
  const hot = rank <= 3;
  return (
    <Box
      sx={{
        width: 26,
        height: 26,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        fontSize: 12,
        fontWeight: 700,
        color: hot ? accent : 'text.secondary',
        bgcolor: (t) => (hot ? alpha(accent, 0.12) : t.palette.action.hover),
      }}
    >
      {rank}
    </Box>
  );
}
